import type {
  EntityTableDisplayColumn,
  EntityTypeDefinition,
} from "@/admin/hooks/use-entities";
import type { Holding } from "@/admin/types/products";
import { addressDisplay, isAddressLike } from "@/shared/lib/formatting";
import {
  formatCurrencyValue,
  formatDurationFromMonths,
  isMoneyObject,
} from "@/admin/lib/formatting";
import { DEFAULT_LOCALE, DEFAULT_CURRENCY } from "@/shared/lib/formatting";
import { plannedWarranty } from "@/admin/lib/warranty-record";

type AttributeValue = {
  code_name: string;
  display_name?: string;
  value: unknown;
};

type DisplayContext = {
  entityKey: string;
  value: unknown;
};

/**
 * Resolved value for a column whose attribute has
 * ``configuration_data.value_source === "workflow_state"``. Carries the
 * full state so a caller can render a ``WorkflowStateBadge`` without
 * re-looking-up ``holding.workflow_states``. Columns that render a plain
 * label (the holding lifecycle ``status`` column, which stays a
 * ``StatusBadge``) resolve to a string instead - see
 * ``resolveWorkflowStateColumnValue``.
 */
export interface WorkflowStateResolution {
  kind: "workflow_state";
  label: string;
  stateKey?: string;
  workflowKey?: string;
  color?: string | null;
  isTerminal?: boolean;
}

export function isWorkflowStateResolution(
  value: unknown,
): value is WorkflowStateResolution {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "workflow_state"
  );
}

export const DEFAULT_LIFECYCLE_STATUS_WORKFLOW_KEY = "lifecycle_status";

export function registrationCardDisplayColumns(
  entity: EntityTypeDefinition | undefined,
): EntityTableDisplayColumn[] {
  return entity?.metadata?.display?.registration_card?.columns ?? [];
}

/** Columns configured via the transaction / purchase template Display tab. */
export function transactionCardDisplayColumns(
  entity: EntityTypeDefinition | undefined,
): EntityTableDisplayColumn[] {
  return entity?.metadata?.display?.purchase_card?.columns ?? [];
}

/**
 * Columns for the purchase / transaction card, gated by the fields the
 * registration step actually collected.
 *
 * Prefers ``purchase_card`` columns; falls back to ``transaction_attribute:``
 * prefixed columns in ``registration_card`` for configs saved before the
 * section was split. Then gates by ``collected_transaction_attribute_codes``
 * so a field the step never captured (data-capture EXCLUDED, hidden, or
 * filtered out by warranty type) is not shown as a blank row. ``null``/absent
 * means no transaction template (no gate) so the columns pass through
 * unchanged - mirrors the review-card gate in ``step-review.tsx``.
 *
 * Shared by the registration detail page and the warranty record pages so the
 * gate stays identical across every surface that renders the purchase card.
 */
export function purchaseCardDisplayColumns(
  holdingEntity: EntityTypeDefinition | undefined,
  collectedTransactionCodes: string[] | null | undefined,
): EntityTableDisplayColumn[] {
  const allColumns =
    transactionCardDisplayColumns(holdingEntity).length > 0
      ? transactionCardDisplayColumns(holdingEntity)
      : registrationCardDisplayColumns(holdingEntity).filter((c) =>
          c.id.startsWith("transaction_attribute:"),
        );
  return Array.isArray(collectedTransactionCodes)
    ? allColumns.filter((c) =>
        collectedTransactionCodes.includes(c.attribute_code_name),
      )
    : allColumns;
}

export function hasRegistrationCardDisplay(
  entity: EntityTypeDefinition | undefined,
) {
  return registrationCardDisplayColumns(entity).length > 0;
}

export function hasTransactionCardDisplay(
  entity: EntityTypeDefinition | undefined,
) {
  return transactionCardDisplayColumns(entity).length > 0;
}

export function displayColumnLabel(column: EntityTableDisplayColumn) {
  return column.label || column.attribute_code_name;
}

export function formatEntityCardValue(
  value: unknown,
  options?: { locale?: string; currency?: string },
): string {
  if (value === null || value === undefined || value === "") return "-";

  // Workflow-state columns resolve to a tagged object (see
  // ``resolveWorkflowStateColumnValue``). Plain-text surfaces (claim detail
  // host/product rows, CSV export, tooltips) render the state label.
  if (isWorkflowStateResolution(value)) return value.label || "-";

  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const values = value
      .map((item) => formatEntityCardValue(item, options))
      .filter((item) => item !== "-");
    return values.length > 0 ? values.join(", ") : "-";
  }
  if (typeof value === "object") {
    if (isAddressLike(value)) {
      const address = addressDisplay(value);
      if (address) return address;
    }
    if (isMoneyObject(value)) {
      return formatCurrencyValue(
        value,
        options?.currency ?? DEFAULT_CURRENCY,
        options?.locale ?? DEFAULT_LOCALE,
      );
    }
    const record = value as Record<string, unknown>;
    if (isEntityFileObject(record)) {
      const fileName = entityFileDisplayName(record);
      if (fileName) return fileName;
    }
    // Entity-reference picker value (an ENTITY_SEARCH capture stores
    // `{ uuid, name }`, or `{ uuid: '', name }` for a free-text "add" entry).
    // Surface the human-readable label instead of dumping the raw dict as JSON.
    // Mirrors `fmtEntityReference` in `shared/lib/forms/display.tsx` so a picked
    // component product renders as its name on the claim's Product card, not
    // `{"uuid":"...","name":"..."}`.
    const referenceLabel = entityReferenceLabel(record);
    if (referenceLabel) return referenceLabel;
    return JSON.stringify(value);
  }
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(value);
      if (isMoneyObject(parsed)) {
        return formatCurrencyValue(
          parsed,
          options?.currency ?? DEFAULT_CURRENCY,
          options?.locale ?? DEFAULT_LOCALE,
        );
      }
      // A HOLDING EAV entity-reference value can arrive as its JSON-string form
      // rather than a parsed object; unwrap it to the label too.
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const label = entityReferenceLabel(parsed as Record<string, unknown>);
        if (label) return label;
      }
    } catch { /* not JSON */ }
  }
  return String(value);
}

export function entityFileDisplayName(value: Record<string, unknown>) {
  if (typeof value.display === "string" && value.display) return value.display;
  const nested = value.value as Record<string, unknown> | undefined;
  const label =
    value.file_name ||
    value.fileName ||
    value.uploaded_file_name ||
    value.name ||
    value.original_filename ||
    nested?.file_name;
  if (label) return String(label);
  if (typeof value.uuid === "string" && (value.file_url || value.fileUrl)) {
    return value.uuid.slice(0, 8);
  }
  if (typeof value.uploaded_file_uuid === "string") {
    return value.uploaded_file_uuid.slice(0, 8);
  }
  return "";
}

export function entityFileDisplayUrl(value: Record<string, unknown>) {
  const nested = value.value as Record<string, unknown> | undefined;
  const url = value.file_url || value.fileUrl || value.url || nested?.file_url;
  return typeof url === "string" && url ? url : "";
}

/**
 * Human-readable label for an entity-reference object (an ENTITY_SEARCH capture,
 * e.g. `{ uuid, name }`). Returns the first non-blank of `display` / `name` /
 * `label`, else a composite `*__name` display column, else null. Mirrors
 * `fmtEntityReference` in `shared/lib/forms/display.tsx`.
 */
export function entityReferenceLabel(
  record: Record<string, unknown> | null | undefined,
): string | null {
  if (!record || typeof record !== "object") return null;
  const label = [record.display, record.name, record.label].find(
    (v): v is string => typeof v === "string" && v.trim().length > 0,
  );
  if (label) return label;
  const dunder = Object.entries(record).find(
    ([k, v]) => k.includes("__") && typeof v === "string" && v.trim().length > 0,
  );
  return dunder ? (dunder[1] as string) : null;
}

export function isEntityFileObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (record.display && typeof record.value === "object" && record.value !== null) {
    const nested = record.value as Record<string, unknown>;
    if (nested.uuid || nested.file_name || nested.file_url) return true;
  }
  return Boolean(
    record.file_url ||
      record.fileUrl ||
      record.file_name ||
      record.fileName ||
      record.uploaded_file_name ||
      record.uploaded_file_uuid,
  );
}

export function resolveHoldingRegistrationCardValue(
  holding: Holding | undefined | null,
  column: EntityTableDisplayColumn,
  options: { statusWorkflowKey?: string; entityTypes?: EntityTypeDefinition[] } = {},
) {
  return resolveRegistrationDisplayColumnValue(holding, column, options);
}

export function resolveRegistrationDisplayColumnValue(
  holding: Holding | undefined | null,
  column: EntityTableDisplayColumn,
  options: { statusWorkflowKey?: string; entityTypes?: EntityTypeDefinition[] } = {},
) {
  if (!holding) return "";
  if (column.id.startsWith("transaction_attribute:")) {
    return getAttributeListValue(holding.transaction_attribute_values, column.attribute_code_name);
  }
  const path = column.relationship_path?.length
    ? column.relationship_path
    : column.relationship_key
      ? [column.relationship_key]
      : [];
  let context = initialRegistrationDisplayContext(holding, column, path);

  for (const relationshipKey of path) {
    const next = resolveHoldingRelationship(holding, context, relationshipKey);
    if (!next) return "";
    context = next;
  }

  const attribute = findEntityAttribute(
    options.entityTypes,
    context.entityKey,
    column.attribute_code_name,
  );
  if (attribute?.source === "COMPUTED") {
    const template = attribute.configuration_data?.default_value;
    if (typeof template === "string" && template.trim()) {
      const resolved = resolveComputedTemplate(holding, context, template, options);
      return applyValueMap(resolved, attribute.configuration_data?.value_map);
    }
  }
  if (
    context.entityKey === "holding" &&
    attribute?.configuration_data?.value_source === "transaction_attribute"
  ) {
    return getAttributeListValue(
      holding.transaction_attribute_values,
      column.attribute_code_name,
    );
  }
  // Generic workflow-state columns (e.g. Payment -> ``lifecycle_payment``,
  // Status -> ``lifecycle_status``). Resolved from the holding's
  // ``workflow_states`` by the attribute's ``workflow_key``. Dispatched off
  // the attribute config here, so it never falls through to the raw
  // record/EAV lookup below (which would render the raw enum or an empty
  // dash instead of the workflow state).
  if (
    context.entityKey === "holding" &&
    attribute?.configuration_data?.value_source === "workflow_state"
  ) {
    const workflowKey =
      attribute.configuration_data.workflow_key || column.attribute_code_name;
    return resolveWorkflowStateColumnValue(holding, workflowKey, options);
  }

  const resolved = resolveContextValue(holding, context, column.attribute_code_name, options);
  // For non-COMPUTED attributes, configuration_data.default_value is a
  // literal fallback for when the EAV/COLUMN value is empty (e.g.
  // ``submitted_from`` defaults to "iWarranty" until an integration
  // overwrites it). COMPUTED attributes already consumed default_value
  // as a template above.
  if (
    (resolved === "" || resolved === null || resolved === undefined)
    && attribute
    && attribute.source !== "COMPUTED"
  ) {
    const fallback = attribute.configuration_data?.default_value;
    if (typeof fallback === "string" && fallback.trim()) {
      return fallback;
    }
  }
  if (attribute?.configuration_data?.value_map && typeof resolved === "string") {
    return applyValueMap(resolved, attribute.configuration_data.value_map);
  }
  return resolved;
}

function initialRegistrationDisplayContext(
  holding: Holding,
  column: EntityTableDisplayColumn,
  path: string[],
): DisplayContext {
  if (path.length === 0) {
    return contextForEntityKey(holding, column.entity_key || "holding");
  }

  return path[0] === "holdings" || path[0] === "holding"
    ? contextForEntityKey(holding, "host")
    : contextForEntityKey(holding, "holding");
}

function contextForEntityKey(holding: Holding, entityKey: string): DisplayContext {
  switch (entityKey) {
    case "host":
      return { entityKey: "host", value: holding.host ?? null };
    case "product":
      return { entityKey: "product", value: holding.product ?? null };
    case "consumer":
      return { entityKey: "consumer", value: holding.consumer_account ?? null };
    case "organisation":
      return { entityKey: "organisation", value: holding.partnership ?? null };
    case "warranty":
      return { entityKey: "warranty", value: warrantyFromHolding(holding) };
    case "warranty_option":
      return { entityKey: "warranty_option", value: warrantyOptionFromHolding(holding) };
    case "warranty_template":
      return { entityKey: "warranty_template", value: warrantyTemplateFromWarranty(warrantyFromHolding(holding)) };
    case "warranty_category":
      return { entityKey: "warranty_category", value: warrantyCategoryFromTemplate(warrantyTemplateFromWarranty(warrantyFromHolding(holding))) };
    case "structure":
      return { entityKey: "structure", value: holding.structure ?? null };
    case "holding":
    default:
      return { entityKey: "holding", value: holding };
  }
}

function findEntityAttribute(
  entityTypes: EntityTypeDefinition[] | undefined,
  entityKey: string,
  codeName: string,
) {
  return entityTypes
    ?.find((entity) => entity.key === entityKey)
    ?.attributes.find((attribute) => attribute.code_name === codeName);
}

function resolveComputedTemplate(
  holding: Holding,
  baseContext: DisplayContext,
  template: string,
  options: { statusWorkflowKey?: string },
) {
  return template.replace(/\{([^}]+)\}/g, (_, token: string) => {
    const value = resolveTemplateToken(holding, baseContext, token, options);
    return value === null || value === undefined ? "" : String(value);
  });
}

// Maps a resolved computed value through the attribute's
// ``configuration_data.value_map`` (raw → display label). Lets seed data
// declare enum-to-label mappings (e.g. MANUFACTURER_WARRANTY → "Standard")
// instead of hardcoding tenant-specific business labels in code.
function applyValueMap(resolved: string, valueMap: unknown): string {
  if (!valueMap || typeof valueMap !== "object" || Array.isArray(valueMap)) {
    return resolved;
  }
  if (resolved === "") return resolved;
  const map = valueMap as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(map, resolved)) {
    const mapped = map[resolved];
    if (typeof mapped === "string") return mapped;
  }
  return resolved;
}

function resolveTemplateToken(
  holding: Holding,
  baseContext: DisplayContext,
  token: string,
  options: { statusWorkflowKey?: string },
) {
  const parts = token
    .split(".")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return "";

  let context = baseContext;
  for (const relationshipKey of parts.slice(0, -1)) {
    const next = resolveHoldingRelationship(holding, context, relationshipKey);
    if (!next) return "";
    context = next;
  }

  return resolveContextValue(holding, context, parts[parts.length - 1], options);
}

function resolveHoldingRelationship(
  holding: Holding,
  context: DisplayContext,
  relationshipKey: string,
): DisplayContext | null {
  if (context.entityKey === "host") {
    if (relationshipKey === "holdings" || relationshipKey === "holding") {
      return { entityKey: "holding", value: holding };
    }
    return null;
  }

  if (context.entityKey === "holding") {
    switch (relationshipKey) {
      case "host":
        return { entityKey: "host", value: holding.host ?? null };
      case "product":
        return { entityKey: "product", value: holding.product ?? null };
      case "consumer":
        return { entityKey: "consumer", value: holding.consumer_account ?? null };
      case "organisation":
        return { entityKey: "organisation", value: holding.partnership ?? null };
      case "warranty":
      case "warranties":
        return { entityKey: "warranty", value: warrantyFromHolding(holding) };
      case "warranty_option":
        return { entityKey: "warranty_option", value: warrantyOptionFromHolding(holding) };
      case "warranty_template":
        return { entityKey: "warranty_template", value: warrantyTemplateFromWarranty(warrantyFromHolding(holding)) };
      case "warranty_category":
        return { entityKey: "warranty_category", value: warrantyCategoryFromTemplate(warrantyTemplateFromWarranty(warrantyFromHolding(holding))) };
      case "structure":
        return { entityKey: "structure", value: holding.structure ?? null };
      default:
        return null;
    }
  }

  if (context.entityKey === "product" && relationshipKey === "product_template") {
    const product = context.value as Record<string, unknown> | null;
    return {
      entityKey: "product_template",
      value: product?.product_template ?? null,
    };
  }

  if (context.entityKey === "warranty" && relationshipKey === "warranty_option") {
    return {
      entityKey: "warranty_option",
      value: warrantyOptionFromWarranty(context.value),
    };
  }

  if (context.entityKey === "warranty" && relationshipKey === "warranty_template") {
    return {
      entityKey: "warranty_template",
      value: warrantyTemplateFromWarranty(context.value),
    };
  }

  if (
    context.entityKey === "warranty_option" &&
    relationshipKey === "warranty_template"
  ) {
    return {
      entityKey: "warranty_template",
      value: warrantyTemplateFromOption(context.value),
    };
  }

  if (
    context.entityKey === "warranty_template" &&
    relationshipKey === "warranty_category"
  ) {
    return {
      entityKey: "warranty_category",
      value: warrantyCategoryFromTemplate(context.value),
    };
  }

  return null;
}

function resolveContextValue(
  holding: Holding,
  context: DisplayContext,
  codeName: string,
  options: { statusWorkflowKey?: string },
) {
  switch (context.entityKey) {
    case "host": {
      const hostRecord = context.value as Record<string, unknown> | null;
      const hostFieldValue = getRecordValue(hostRecord, codeName);
      if (codeName === "address" && isAddressLike(hostFieldValue)) {
        return addressDisplay(hostFieldValue);
      }
      return getAttributeListValue(
        (hostRecord as { attribute_values?: AttributeValue[] } | null)
          ?.attribute_values,
        codeName,
      ) || hostFieldValue;
    }
    case "holding":
      if (codeName === "status") {
        // Fallback path for callers that don't pass ``entityTypes`` (so the
        // ``value_source: workflow_state`` dispatch in
        // ``resolveRegistrationDisplayColumnValue`` couldn't fire). Resolve the
        // lifecycle status the same way, keyed by the configured status
        // workflow key.
        return resolveWorkflowStateColumnValue(
          holding,
          options.statusWorkflowKey ?? DEFAULT_LIFECYCLE_STATUS_WORKFLOW_KEY,
          options,
        );
      }
      return getAttributeListValue(holding.attribute_values, codeName)
        || getRecordValue(holding.registration_field_values, codeName)
        || getRecordValue(context.value, codeName);
    case "product":
      return getProductValue(context.value, codeName);
    case "consumer":
      return getConsumerValue(context.value, codeName);
    case "organisation":
      if (codeName === "name") {
        return (
          holding.partnership?.downstream_organisation_name ||
          (holding as unknown as Record<string, unknown>).dealer_name ||
          holding.organisation?.name ||
          ""
        );
      }
      return getRecordValue(context.value, codeName);
    case "warranty":
      return getWarrantyValue(context.value, codeName);
    case "warranty_option":
      return getWarrantyOptionValue(context.value, codeName);
    case "warranty_template":
      return getWarrantyTemplateValue(context.value, codeName);
    case "structure":
    case "product_template":
    case "warranty_category":
      return getRecordValue(context.value, codeName);
    default:
      return "";
  }
}

function getAttributeListValue(
  values: AttributeValue[] | undefined,
  codeName: string,
) {
  const value = values?.find((attr) => attr.code_name === codeName)?.value;
  return value === null || value === undefined || value === "" ? "" : value;
}

function getRecordValue(record: unknown, codeName: string) {
  if (!record || typeof record !== "object") return "";
  const value = (record as Record<string, unknown>)[codeName];
  return value === null || value === undefined || value === "" ? "" : value;
}

function getProductValue(product: unknown, codeName: string) {
  if (!product || typeof product !== "object") return "";
  const record = product as Record<string, unknown>;
  if (codeName === "name") {
    return record.cached_short_name || record.cached_long_name || "";
  }
  if (codeName === "line_name") {
    // The product line: a variant's parent name, else the product's own name.
    // A tenant whose variant names repeat an attribute (e.g. the colour) picks
    // this column instead of `name`; `name` stays the product's own name.
    const dataName = (record.product_data as Record<string, unknown> | undefined)
      ?.name;
    if (typeof dataName === "string" && dataName.trim()) return dataName;
    const parentName = (
      record.parent_product as Record<string, unknown> | undefined
    )?.cached_short_name;
    if (typeof parentName === "string" && parentName.trim()) return parentName;
    return record.cached_short_name || record.cached_long_name || "";
  }
  if (codeName === "product_template") {
    const template = record.product_template as { name?: string } | undefined;
    return template?.name ?? "";
  }
  if (codeName === "category") {
    const category = record.category as { name?: string } | undefined;
    return category?.name ?? "";
  }
  return (
    getAttributeListValue(
      record.attribute_values as AttributeValue[] | undefined,
      codeName,
    )
    || getRecordValue(product, codeName)
    || getRecordValue(record.product_data, codeName)
  );
}

function getConsumerValue(consumer: unknown, codeName: string) {
  if (!consumer || typeof consumer !== "object") return "";
  const record = consumer as Record<string, unknown>;
  if (codeName === "name") {
    return [record.first_name, record.last_name].filter(Boolean).join(" ");
  }
  if (codeName === "phone") return record.phone_number ?? "";
  // Composite ADDRESS: individual component values are stored as
  // separate EAV rows (address_line_1, address_city, …). Compose
  // them into a display string using the same structure as
  // ``addressDisplay``.
  if (codeName === "address") {
    const attrValues = record.attribute_values as AttributeValue[] | undefined;
    // Preferred: a single composite ADDRESS attribute stored under its own
    // code_name, whose value is the canonical ``{display, value}`` envelope
    // (or a bare AddressValue). ``addressDisplay`` reads either shape.
    const single = attrValues?.find((av) => av.code_name === codeName)?.value;
    if (single) {
      const display = addressDisplay(single);
      if (display) return display;
    }
    // Legacy: address split across individual component EAV rows
    // (address_line_1, address_city, …).
    if (attrValues?.length) {
      const byCode: Record<string, unknown> = {};
      for (const av of attrValues) {
        if (av.code_name.startsWith("address_")) {
          byCode[av.code_name] = av.value;
        }
      }
      if (Object.keys(byCode).length > 0) {
        // Build address_line_1/address_line_2/locality/region/postalCode
        // envelope for addressDisplay() using the canonical component
        // column mapping from the consumer entity's ADDRESS attribute.
        const envelope = {
          address_line_1: byCode.address_line_1 ?? "",
          address_line_2: byCode.address_line_2 ?? "",
          locality: byCode.address_city ?? "",
          region: byCode.address_state ?? "",
          postal_code: byCode.address_postcode ?? "",
          country_code: byCode.address_country ?? "",
        };
        const display = addressDisplay(envelope);
        if (display) return display;
      }
    }
    return "";
  }
  // Try direct field first, then fall through to EAV attribute_values
  // (custom consumer fields). Same pattern as getWarrantyValue.
  const direct = getRecordValue(consumer, codeName);
  if (direct !== "" && direct !== null && direct !== undefined) return direct;
  return getAttributeListValue(
    record.attribute_values as AttributeValue[] | undefined,
    codeName,
  ) || direct;
}

const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: "Standard",
  RETAILER_WARRANTY: "Retailer",
  EXTENDED_WARRANTY: "Extended",
};

const COVERAGE_SCOPE_LABELS: Record<string, string> = {
  MATERIAL_ONLY: "Materials Only",
  LABOR_ONLY: "Labor Only",
  MATERIAL_AND_LABOR: "Materials & Labor",
};

function getWarrantyValue(warranty: unknown, codeName: string) {
  if (!warranty || typeof warranty !== "object") return "";
  const record = warranty as Record<string, unknown>;
  if (codeName === "name") {
    return record.option_display_name || record.class_name || record.warranty_type || "";
  }
  if (codeName === "warranty_type") {
    const raw = String(record.warranty_type ?? "");
    return WARRANTY_TYPE_LABELS[raw] ?? raw;
  }
  if (codeName === "coverage_scope") {
    const raw = String(record.coverage_scope ?? "");
    return COVERAGE_SCOPE_LABELS[raw] ?? raw;
  }
  if (codeName === "start_date") return record.active_from ?? "";
  // The live answer (counts transfers already used) beats the copy stamped at issue.
  if (codeName === "is_transferable" && typeof record.is_transferable === "boolean") {
    return record.is_transferable;
  }
  if (codeName === "end_date") {
    // Lifetime warranties have no expiry - render "Lifetime" rather than
    // an empty value (which would surface as a placeholder dash).
    if (record.is_lifetime) return "Lifetime";
    if (record.expires_at) return record.expires_at;
    const activeFrom = record.active_from;
    const months = record.duration_in_months;
    if (typeof activeFrom === "string" && typeof months === "number" && months > 0) {
      const start = new Date(activeFrom);
      if (!isNaN(start.getTime())) {
        start.setMonth(start.getMonth() + months);
        return start.toISOString();
      }
    }
    return "";
  }
  if (codeName === "duration_months") {
    if (record.is_lifetime) return "Lifetime";
    const months = record.duration_in_months;
    if (typeof months !== "number" || months <= 0) return "";
    return formatDurationFromMonths(months);
  }
  return getAttributeListValue(
    record.attribute_values as AttributeValue[] | undefined,
    codeName,
  ) || getRecordValue(warranty, codeName);
}

function warrantyFromHolding(holding: Holding) {
  return holding.warranties?.[0] ?? plannedWarranty(holding);
}

function warrantyOptionFromHolding(holding: Holding) {
  const warranty = warrantyFromHolding(holding);
  if (warranty) return warrantyOptionFromWarranty(warranty);
  return null;
}

function warrantyOptionFromWarranty(warranty: unknown) {
  if (!warranty || typeof warranty !== "object") return null;
  const record = warranty as Record<string, unknown>;
  const option = record.warranty_template_option;
  if (option && typeof option === "object") return option;

  return {
    uuid: record.warranty_template_option_uuid,
    display_name: record.option_display_name,
    duration_in_months: record.duration_in_months,
    is_lifetime: record.is_lifetime,
    price_amount: record.price_amount,
    currency: record.currency,
    display_order: record.display_order,
    attribute_overrides: record.attribute_overrides,
  };
}

function warrantyTemplateFromWarranty(warranty: unknown) {
  if (!warranty || typeof warranty !== "object") return null;
  const record = warranty as Record<string, unknown>;
  const template = record.warranty_template;
  if (template && typeof template === "object") return template;

  return {
    name: record.warranty_template_name || record.class_name,
    class_name: record.class_name,
    warranty_type: record.warranty_type,
    coverage_scope: record.coverage_scope,
    is_transferable: record.is_transferable,
    transfer_fee: record.transfer_fee,
    transfer_window_days: record.transfer_window_days,
  };
}

function warrantyTemplateFromOption(option: unknown) {
  if (!option || typeof option !== "object") return null;
  const record = option as Record<string, unknown>;
  const template = record.warranty_template;
  return template && typeof template === "object" ? template : null;
}

function warrantyCategoryFromTemplate(template: unknown) {
  if (!template || typeof template !== "object") return null;
  const record = template as Record<string, unknown>;
  const category = record.warranty_category;
  if (category && typeof category === "object") return category;
  if (typeof record.class_name === "string") return { name: record.class_name };
  return null;
}

function getWarrantyOptionValue(option: unknown, codeName: string) {
  if (!option || typeof option !== "object") return "";
  const record = option as Record<string, unknown>;
  if (codeName === "name") return record.display_name ?? "";
  if (codeName === "duration_in_months" || codeName === "duration_months") {
    // Lifetime warranties carry no month count - surface "Lifetime" rather
    // than an empty value (which renders as a placeholder dash).
    if (record.is_lifetime) return "Lifetime";
    return record.duration_in_months ?? "";
  }
  return getRecordValue(record, codeName);
}

function getWarrantyTemplateValue(template: unknown, codeName: string) {
  if (!template || typeof template !== "object") return "";
  const record = template as Record<string, unknown>;
  if (codeName === "warranty_type") {
    const raw = String(record.warranty_type ?? "");
    return WARRANTY_TYPE_LABELS[raw] ?? raw;
  }
  if (codeName === "coverage_scope") {
    const raw = String(record.coverage_scope ?? "");
    return COVERAGE_SCOPE_LABELS[raw] ?? raw;
  }
  return getRecordValue(record, codeName);
}

/**
 * Resolve a workflow-state column value from ``holding.workflow_states``.
 *
 * Two shapes are returned by design:
 *
 * - The holding **lifecycle status** column (``workflowKey`` equals the
 *   configured status workflow key) returns a plain **string** label so it
 *   keeps rendering through ``StatusBadge`` exactly as before. ``Holding.status``
 *   is a real enum present on every holding; ``cancelled``/``transferred`` are
 *   permanent facts the lifecycle workflow never models, so they take
 *   precedence over any workflow state (matching the backend's
 *   ``_uuids_matching_status_key``). When no lifecycle workflow instance
 *   exists we fall back to the enum label (``Draft``/``Registered``). The old
 *   ``warranty_summary.status`` scrape is deliberately dropped.
 * - Every **other** workflow column (e.g. Payment) returns a
 *   {@link WorkflowStateResolution} object so callers render a coloured
 *   ``WorkflowStateBadge``; it resolves to ``""`` when no matching instance
 *   exists (no enum fallback - those columns have no ``Holding`` enum overlay).
 */
function resolveWorkflowStateColumnValue(
  holding: Holding,
  workflowKey: string,
  options: { statusWorkflowKey?: string },
): WorkflowStateResolution | string {
  const state = holding.workflow_states?.find(
    (candidate) => candidate.workflow_key === workflowKey,
  );

  const statusWorkflowKey =
    options.statusWorkflowKey ?? DEFAULT_LIFECYCLE_STATUS_WORKFLOW_KEY;
  if (workflowKey === statusWorkflowKey) {
    if (holding.status === "cancelled") return "Cancelled";
    if (holding.status === "transferred") return "Transferred";
    if (state?.state_label) return state.state_label;
    if (holding.status === "draft") return "Draft";
    if (holding.status === "registered") return "Registered";
    return holding.status ?? "";
  }

  if (!state) return "";
  return {
    kind: "workflow_state",
    label: state.state_label || state.state_key,
    stateKey: state.state_key,
    workflowKey: state.workflow_key,
    color: state.state_color,
    isTerminal: state.is_terminal,
  };
}
